/**
 * @quotech/auth - Integration Status Sync
 * 
 * PATH: packages/auth/src/integrationsSync.ts
 * PURPOSE: Sync integration status (Xero, Simpro, OneDrive, Email) from backend to cookie
 * 
 * FEATURES:
 * - Fetches integration status from backend API
 * - Caches status in shared domain cookie (quotech_integrations)
 * - Provides instant access to integration status without API calls
 * - Tracks Xero token expiration for reconnection prompts
 * 
 * USAGE:
 * ```typescript
 * import { syncIntegrationStatus, getIntegrationStatus, isXeroExpired } from '@quotech/auth'
 * 
 * // After login or periodically
 * await syncIntegrationStatus(accessToken)
 * 
 * // Check status (instant, from cookie)
 * const status = getIntegrationStatus()
 * if (status?.xero_connected && !isXeroExpired()) {
 *   // Xero is connected and valid
 * }
 * ```
 */

import { useState, useEffect, useCallback } from 'react'
import {
  getAuthCookie,
  setIntegrationsCookie,
  getIntegrationsCookie,
  isXeroExpired as checkXeroExpired,
  getOrgCookie,
  type IntegrationStatus,
} from './cookieAuth'

// =============================================================================
// CONSTANTS
// =============================================================================

const API_BASE = `${import.meta.env.VITE_API_URL || 'https://quotech-backend.quotech.ai'}/api`

// How often to auto-refresh integration status (1 hour)
const SYNC_INTERVAL_MS = 60 * 60 * 1000

// Cache key for last sync time
const LAST_SYNC_KEY = 'quotech:integrations_last_sync'

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Fetch integration status from backend
 * @param token - JWT access token (optional, uses cookie if not provided)
 * @returns IntegrationStatus or null if fetch fails
 */
export async function fetchIntegrationStatus(token?: string): Promise<IntegrationStatus | null> {
  const accessToken = token || getAuthCookie()
  
  if (!accessToken) {
    console.warn('[@quotech/auth] Cannot fetch integration status: no access token')
    return null
  }

  try {
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    }

    const orgCookie = getOrgCookie()
    if (orgCookie?.id) {
      headers['X-Organisation-ID'] = orgCookie.id
    }

    const response = await fetch(`${API_BASE}/organisations/integration-status/`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      // If 404, endpoint doesn't exist yet - return default status
      if (response.status === 404) {
        console.log('[@quotech/auth] Integration status endpoint not available')
        return getDefaultIntegrationStatus()
      }
      
      console.error('[@quotech/auth] Failed to fetch integration status:', response.status)
      return null
    }

    const data = await response.json()
    
    return {
      xero_connected: data.xero_connected ?? false,
      xero_tenant_name: data.xero_tenant_name ?? null,
      xero_expires_at: data.xero_expires_at ?? null,
      simpro_connected: data.simpro_connected ?? false,
      simpro_company_id: data.simpro_company_id ?? null,
      onedrive_connected: data.onedrive_connected ?? false,
      email_connected: data.email_connected ?? false,
      last_sync: new Date().toISOString(),
    }
  } catch (error) {
    console.error('[@quotech/auth] Error fetching integration status:', error)
    return null
  }
}

/**
 * Get default integration status (all disconnected)
 */
function getDefaultIntegrationStatus(): IntegrationStatus {
  return {
    xero_connected: false,
    xero_tenant_name: null,
    xero_expires_at: null,
    simpro_connected: false,
    simpro_company_id: null,
    onedrive_connected: false,
    email_connected: false,
    last_sync: new Date().toISOString(),
  }
}

// =============================================================================
// SYNC FUNCTIONS
// =============================================================================

/**
 * Sync integration status from backend to cookie
 * @param token - JWT access token (optional, uses cookie if not provided)
 * @param force - Force sync even if recently synced
 * @returns IntegrationStatus or null if sync fails
 */
export async function syncIntegrationStatus(
  token?: string,
  force = false
): Promise<IntegrationStatus | null> {
  // Check if we should skip sync (recently synced)
  if (!force && !shouldSync()) {
    const cached = getIntegrationsCookie()
    if (cached) {
      console.log('[@quotech/auth] Using cached integration status')
      return cached
    }
  }

  const status = await fetchIntegrationStatus(token)
  
  if (status) {
    // Update cookie
    setIntegrationsCookie(status)
    
    // Record sync time
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(LAST_SYNC_KEY, Date.now().toString())
    }
    
    console.log('[@quotech/auth] Integration status synced')
  }
  
  return status
}

/**
 * Check if we should sync (hasn't synced recently)
 */
function shouldSync(): boolean {
  if (typeof localStorage === 'undefined') return true
  
  const lastSync = localStorage.getItem(LAST_SYNC_KEY)
  if (!lastSync) return true
  
  const elapsed = Date.now() - parseInt(lastSync, 10)
  return elapsed >= SYNC_INTERVAL_MS
}

// =============================================================================
// GETTER FUNCTIONS (from cookie - instant, no API call)
// =============================================================================

/**
 * Get integration status from cookie (instant, no API call)
 * @returns IntegrationStatus or null if not cached
 */
export function getIntegrationStatus(): IntegrationStatus | null {
  return getIntegrationsCookie()
}

/**
 * Check if Xero is connected and token is valid
 */
export function isXeroConnected(): boolean {
  const status = getIntegrationsCookie()
  return status?.xero_connected === true && !checkXeroExpired()
}

/**
 * Check if Xero token is expired (re-export)
 */
export { checkXeroExpired as isXeroExpired }

/**
 * Get Xero tenant name
 */
export function getXeroTenantName(): string | null {
  const status = getIntegrationsCookie()
  return status?.xero_tenant_name ?? null
}

/**
 * Check if Simpro is connected
 */
export function isSimproConnected(): boolean {
  const status = getIntegrationsCookie()
  return status?.simpro_connected === true
}

/**
 * Get Simpro company ID
 */
export function getSimproCompanyId(): string | null {
  const status = getIntegrationsCookie()
  return status?.simpro_company_id ?? null
}

/**
 * Check if OneDrive is connected
 */
export function isOneDriveConnected(): boolean {
  const status = getIntegrationsCookie()
  return status?.onedrive_connected === true
}

/**
 * Check if Email sync is enabled
 */
export function isEmailConnected(): boolean {
  const status = getIntegrationsCookie()
  return status?.email_connected === true
}

// =============================================================================
// REACT HOOK
// =============================================================================

/**
 * React hook for integration status
 * Syncs on mount and provides cached status
 */
export function useIntegrationStatus(): {
  status: IntegrationStatus | null
  isLoading: boolean
  refresh: () => Promise<void>
  xero: { connected: boolean; expired: boolean; tenantName: string | null }
  simpro: { connected: boolean; companyId: string | null }
  onedrive: { connected: boolean }
  email: { connected: boolean }
} {
  const [status, setStatus] = useState<IntegrationStatus | null>(() => getIntegrationsCookie())
  const [isLoading, setIsLoading] = useState(false)

  const refresh = useCallback(async () => {
    setIsLoading(true)
    try {
      const newStatus = await syncIntegrationStatus(undefined, true)
      if (newStatus) {
        setStatus(newStatus)
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Sync on mount
  useEffect(() => {
    const doSync = async () => {
      setIsLoading(true)
      try {
        const newStatus = await syncIntegrationStatus()
        if (newStatus) {
          setStatus(newStatus)
        }
      } finally {
        setIsLoading(false)
      }
    }
    doSync()
  }, [])

  return {
    status,
    isLoading,
    refresh,
    xero: {
      connected: status?.xero_connected ?? false,
      expired: checkXeroExpired(),
      tenantName: status?.xero_tenant_name ?? null,
    },
    simpro: {
      connected: status?.simpro_connected ?? false,
      companyId: status?.simpro_company_id ?? null,
    },
    onedrive: {
      connected: status?.onedrive_connected ?? false,
    },
    email: {
      connected: status?.email_connected ?? false,
    },
  }
}

